import React, { useEffect, useState } from 'react';
import type { FC } from 'react';
import { Box, Dialog, DialogActions, DialogContent, Grid, CircularProgress } from '@mui/material';
import styles from './SwitchProduction.module.scss';
import ModalIcon from '../ModalIcon/ModalIcon.lazy';
import { ModalIcons } from '../ModalIcon/ModalIcon';
import type { RootState } from '../../../state/store';
import SuccessButton from '../../common/buttons/SuccessButton';
import TertiaryButton from '../../common/buttons/TertiaryButton';
import ModalService from '../../../services/ModalService';
import ModalTitle from '../ModalTitle/ModalTitle.lazy';
import dialogStyles from '../../../theme/dialog.module.scss';
import { SwitchProductionModal } from './constants';
import type { AuthState } from '../../../state/slices/AuthReducer';
import FormInputSelect from '../../forms/FormInputSelect';
import OptionPropsService from '../../../services/OptionPropsService';
import ProductionService from '../../../services/production/ProductionService';
import progressStyles from '../../../theme/loadingProgress.module.scss';
import { useSelector } from 'react-redux';
import type { UseTranslationResponse } from 'react-i18next';
import { useTranslation } from 'react-i18next';
import { useForm } from 'react-hook-form';

/**
 * Switch Production Modal
 */
const SwitchProduction: FC = () => {
  const { t }: UseTranslationResponse<'translation', undefined> = useTranslation();
  const modalsState = useSelector((state: RootState) => state.modals.custom[SwitchProductionModal]);
  const authState: AuthState = useSelector((state: RootState) => state.auth);

  const [errorValue, setErrorValue] = useState<string>('');
  const [sortedCommunities, setSortedCommunities] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [showNoAccess, setShowNoAccess] = useState<string>('');

  const defaultValues = {
    newCommunity: ''
  }

  const { control, reset, getValues } = useForm({
    defaultValues
  });

  useEffect(() => {
    if (modalsState.isVisible) {
      if (modalsState.metaData) {
        setShowNoAccess(modalsState.metaData[0]);
      }
    }
  }, [modalsState.isVisible]);

  useEffect(() => {
    if (authState.otherAvailableProductions.length > 0) {
      const otherProductions: string[] = [...authState.otherAvailableProductions];
      setSortedCommunities(otherProductions.sort());
    }
  }, [authState.otherAvailableProductions])

  const handleClose = (): void => {
    ModalService.closeCustomModal(SwitchProductionModal);
    setErrorValue('');
    setShowNoAccess('');
    reset({ newCommunity: '' });
    if (modalsState.callback) {
      modalsState.callback(false);
    }
  }

  const handleConfirm = (): void => {
    setErrorValue('');
    const newCommunity: string = getValues('newCommunity');
    if (!newCommunity) {
      setErrorValue('production.switch.error.empty');
      return;
    }
    setIsLoading(true);
    ProductionService.switchProduction(newCommunity)
      .then(() => {
        if (modalsState.callback) {
          modalsState.callback(true);
        }
      })
      .catch((err) => {
        setIsLoading(false);
        console.error(err);
        setErrorValue(err.message);
      });
  }
  return (
    <>
    {(isLoading)
      ? <CircularProgress className={progressStyles.circularProgress} />
      : <></>
    }
    <Dialog
      data-testid="SwitchProduction"
      classes={{
        root: `${dialogStyles.dialogRoot} ${styles.SwitchProduction}`,
        paper: `${dialogStyles.dialogPaper} ${dialogStyles.dialogGeneralWidth}`
      }}
      BackdropProps={{
        className: dialogStyles.dialogBackdrop
      }}
      open={modalsState.isVisible}
      onClose={handleClose}
    >
      <ModalTitle handleClose={handleClose} title={modalsState.title} />
      <DialogContent className={styles.dialogContent}>
        <Grid container spacing={2} className={dialogStyles.dialogContent}>
          <ModalIcon iconType={ModalIcons.Question} />
          <Grid item className={dialogStyles.headerGrid}>
            <Box component={'span'} className={styles.header}>{t(modalsState.heading)}</Box>
          </Grid>
          {(showNoAccess) && <Grid item>
            <Box component={'span'} className={styles.content}>
              {t('production.switch.noAccess', { production: showNoAccess })}
            </Box>
          </Grid>}
          <Grid item>
            <Box component={'span'} className={styles.content}>
              {t(modalsState.content)}
            </Box>
          </Grid>
          <Grid item className={styles.input}>
            <FormInputSelect
              name={'newCommunity'}
              formItemProps={{
                control
              }}
              options={OptionPropsService.productionsToOptionProps(sortedCommunities)}
              width={'100%'}
            />
          </Grid>
          {
            (!!errorValue) &&
            <Grid item>
              <Box
                component={'span'}
                className={dialogStyles.errorField}
              >
                {t(errorValue, { community: String(getValues('newCommunity')) })}
              </Box>
            </Grid>
          }
        </Grid>
      </DialogContent>
      <DialogActions className={dialogStyles.dialogActionsCenter}>
        <TertiaryButton
          disableRipple
          onClick={handleClose}
          className={dialogStyles.cancelButton}
        >
          {t('action.cancel')}
        </TertiaryButton>
        <SuccessButton disableRipple onClick={handleConfirm}>{t(modalsState.confirmButton)}</SuccessButton>
      </DialogActions>
    </Dialog>
    </>
  )
};

export default SwitchProduction;
